import React from 'react';
import { TagType } from '@enter/core';
import styled from '@emotion/styled';
import { transparentize } from 'polished';
import { Variables } from '../base/variables';

const TagContainer = styled.section`
    transition: background 0.2s linear;
    display: inline-flex;
    flex-wrap: wrap;
    max-height: 120px;
    overflow-y: auto;
    cursor: pointer;
    border: 1px solid ${Variables.Colors.nx4};
    border-radius: ${Variables.Rounded.small};

    &:hover {
        background: ${Variables.Colors.white};
    }
`;

const TagItem = styled.div`
    padding: 2px 6px;
    font-size: 14px;
    border-radius: ${Variables.Rounded.medium};
    margin: 2px;
`;

const SystemTagItem = styled(TagItem)`
    background: ${transparentize(0.75, Variables.Colors.r40)};
    color: ${Variables.Colors.r50};
`;

const UserTagItem = styled(TagItem)`
    background: ${transparentize(0.75, Variables.Colors.rose10)};
    color: ${Variables.Colors.rose20};
`;

interface DynamicDetailsProps {
    data: any;
}

const KeyValueRow = ({ label, value }: { label: string; value: any }) => {
    if (value === null || value === undefined) return null;
    return (
        <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '8px' }}>
            <div style={{ fontWeight: 600, fontSize: '16px', minWidth: '120px', marginRight: '8px' }}>
                {label}:
            </div>
            <div style={{ fontSize: '16px', flex: 1 }}>
                {value}
            </div>
        </div>
    );
};

const renderValue = (value: any): any => {
    if (value === null || value === undefined) return 'N/A';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'string' || typeof value === 'number') return value === '' ? 'N/A' : value;
    if (Array.isArray(value)) return value.length === 0 ? 'N/A' : JSON.stringify(value, null, 2);
    if (typeof value === 'object') return JSON.stringify(value, null, 2);
    return value;
};

export const DynamicDetails: React.FC<DynamicDetailsProps> = ({ data }) => {
    if (!data || typeof data !== 'object') {
        return (
            <div style={{ color: Variables.Colors.n70, fontSize: Variables.FontSizes.small, paddingLeft: '24px' }}>No information available.</div>
        );
    }

    const fields = Object.keys(data).map(key => {
        const value = (data as any)[key];
        // Convert camelCase to Title Case
        const label = key
            .replace(/([A-Z])/g, ' $1')
            .replace(/^./, str => str.toUpperCase())
            .trim();

        if (key === 'tags' && Array.isArray(value)) {
            return (
                <div key={key} style={{ marginBottom: '12px', display: 'flex', alignItems: 'flex-start' }}>
                    <div style={{ fontWeight: 600, fontSize: '16px', minWidth: '120px', marginRight: '8px' }}>
                        {label}:
                    </div>
                    <div style={{ flex: 1 }}>
                        {value.length === 0 ? (
                            <div style={{ fontStyle: 'italic', color: Variables.Colors.n70 }}>N/A</div>
                        ) : (
                            <TagContainer>
                                {value.map((t: any) => {
                                    const TagComponent = t.type === TagType.SYSTEM ? SystemTagItem : UserTagItem;
                                    return (
                                        <TagComponent key={`${t.id || t.name}`}>
                                            {t.name}
                                        </TagComponent>
                                    );
                                })}
                            </TagContainer>
                        )}
                    </div>
                </div>
            );
        }

        if (key === 'addresses' && Array.isArray(value) && value.length > 0) {
            return (
                <div key={key} style={{ marginBottom: '12px' }}>
                    <div style={{ fontWeight: 600, fontSize: '16px', marginBottom: '6px' }}>{label}:</div>
                    {value.map((address: any, idx: number) => (
                        <div key={idx} style={{ marginBottom: '8px', padding: '8px', border: '1px solid #e0e0e0', borderRadius: '4px', backgroundColor: '#f9f9f9' }}>
                            <KeyValueRow label="Name" value={address?.name || 'N/A'} />
                            <KeyValueRow label="Type" value={address?.type || 'N/A'} />
                            <KeyValueRow label="Lines" value={address?.lines && address.lines.length ? address.lines.join(', ') : 'N/A'} />
                            <KeyValueRow label="City" value={address?.city || 'N/A'} />
                            <KeyValueRow label="State" value={address?.state || 'N/A'} />
                            <KeyValueRow label="Postal" value={address?.postal || 'N/A'} />
                            <KeyValueRow label="Country" value={address?.country || 'N/A'} />
                        </div>
                    ))}
                </div>
            );
        }

        if (typeof value === 'object' && !Array.isArray(value)) return null;
        return <KeyValueRow key={key} label={label} value={renderValue(value)} />;
    }).filter(Boolean);

    return (
        <div style={{ background: Variables.Colors.white, padding: '12px 12px 12px 24px', borderRadius: Variables.Rounded.small, overflow: 'auto', maxHeight: '70vh', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {fields}
        </div>
    );
};

